import { AlertTitle } from "@mui/material";

import { Collapse } from "ui/collapse/collapse";
import { AlertCloseButton, AlertToggleButton } from "ui/alert/alert-action";
import { PROGRESS_LABEL } from "ui/alert/alert.constants";
import { AlertActions, AlertBody, AlertProgress, AlertRoot } from "ui/alert/alert.styles";
import { type AlertProps } from "ui/alert/alert.types";
import { useAlert } from "ui/alert/use-alert";

export const Alert = ({
  severity = "success",
  title,
  actions,
  progress,
  collapsible = false,
  expanded,
  defaultExpanded,
  onExpandedChange,
  onClose,
  children,
  sx,
  ref,
  ...rest
}: AlertProps) => {
  const { titleId, bodyId, isExpanded, handleToggle, progressValue, hasProgress } = useAlert({
    collapsible,
    expanded,
    defaultExpanded,
    onExpandedChange,
    progress,
  });

  const body = (
    <>
      {children}
      {actions && <AlertActions>{actions}</AlertActions>}
    </>
  );

  return (
    <AlertRoot
      {...rest}
      ref={ref}
      severity={severity}
      isCollapsible={collapsible}
      isCollapsed={!isExpanded}
      hasProgress={hasProgress}
      action={
        collapsible ? (
          <AlertToggleButton isExpanded={isExpanded} bodyId={bodyId} onToggle={handleToggle} />
        ) : (
          onClose && <AlertCloseButton onClose={onClose} />
        )
      }
      sx={sx}>
      {title && <AlertTitle id={titleId}>{title}</AlertTitle>}
      {collapsible ? (
        <Collapse in={isExpanded} id={bodyId}>
          <AlertBody>{body}</AlertBody>
        </Collapse>
      ) : (
        body
      )}
      {progressValue !== undefined && (
        <AlertProgress
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressValue}
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : PROGRESS_LABEL}
          severity={severity}
          value={progressValue}
        />
      )}
    </AlertRoot>
  );
};
